import { useAuth } from '@clerk/clerk-react';
import { Download, Receipt } from 'lucide-react';
import { useMemo, useState } from 'react';
import { API_BASE_URL } from '../api/client.js';
import { useMe } from '../api/useMe.js';
import { type ReimbursementRow, useReimbursementReport } from '../api/useReports.js';
import { useTeamsList } from '../api/useTeams.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';

// Reimbursement report page.
//
// For each worker on each day, correlates their driving journeys with their
// visits to compute a reimbursable distance. Each journey is classified into
// one of four buckets:
//   • Inter-visit  → reimbursable (visit-to-visit driving)
//   • Office bonus → reimbursable (first/last leg touching an office site)
//   • Commute      → excluded (home → first visit / last visit → home)
//   • Unclassified → excluded by default (driving on a day with no visits;
//                     surfaced so payroll can manually override)
//
// Auth gate is the same as Travel: requireCapability('monitor','admin','teamLeader').

const DAY_MS = 24 * 60 * 60_000;

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function dateToWindowIso(dateStr: string, endOfDay: boolean): string {
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

type View = 'by_worker' | 'by_day';

interface WorkerSummary {
  user_id: string;
  display_name: string;
  email: string;
  job_title: string | null;
  daysWorked: number;
  visitCount: number;
  journeyCount: number;
  totalDistanceM: number;
  interVisitM: number;
  officeBonusM: number;
  commuteM: number;
  unclassifiedM: number;
  reimbursableM: number;
}

// Aggregate the per-day rows the API returns into one summary row per worker.
// Days-worked = count of distinct dates that had at least one visit. (Days
// with only unclassified driving and no visits don't count as "worked".)
function summariseByWorker(rows: ReimbursementRow[]): WorkerSummary[] {
  const byUser = new Map<string, WorkerSummary>();
  for (const r of rows) {
    let s = byUser.get(r.user_id);
    if (!s) {
      s = {
        user_id: r.user_id,
        display_name: r.display_name,
        email: r.email,
        job_title: r.job_title,
        daysWorked: 0,
        visitCount: 0,
        journeyCount: 0,
        totalDistanceM: 0,
        interVisitM: 0,
        officeBonusM: 0,
        commuteM: 0,
        unclassifiedM: 0,
        reimbursableM: 0,
      };
      byUser.set(r.user_id, s);
    }
    if (r.visit_count > 0) s.daysWorked += 1;
    s.visitCount += r.visit_count;
    s.journeyCount += r.journey_count;
    s.totalDistanceM += r.total_distance_m;
    s.interVisitM += r.inter_visit_m;
    s.officeBonusM += r.office_bonus_m;
    s.commuteM += r.commute_m;
    s.unclassifiedM += r.unclassified_m;
    s.reimbursableM += r.reimbursable_m;
  }
  return [...byUser.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
}

export default function ReimbursementReport() {
  const me = useMe();
  const teams = useTeamsList();
  const allTeams = teams.data?.teams ?? [];

  // Default window: previous 14 days. Reimbursement is generally run
  // fortnightly, so this matches the cadence.
  const today = new Date();
  const twoWeeksAgo = new Date(today.getTime() - 14 * DAY_MS);
  const [fromDate, setFromDate] = useState(toDateInputValue(twoWeeksAgo));
  const [toDate, setToDate] = useState(toDateInputValue(today));
  const [teamId, setTeamId] = useState<string>('');
  // Default to By worker — that's the payroll-ready view. By day is the
  // detail breakdown when an operator needs to dig in.
  const [view, setView] = useState<View>('by_worker');

  const fromIso = useMemo(() => dateToWindowIso(fromDate, false), [fromDate]);
  const toIso = useMemo(() => dateToWindowIso(toDate, true), [toDate]);

  const report = useReimbursementReport(
    { from: fromIso, to: toIso, teamIds: teamId || undefined },
    Boolean(me.data),
  );
  const rows = report.data?.rows ?? [];
  const workerSummaries = useMemo(() => summariseByWorker(rows), [rows]);
  const totalReimbM = rows.reduce((acc, r) => acc + r.reimbursable_m, 0);
  const totalTotalM = rows.reduce((acc, r) => acc + r.total_distance_m, 0);

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Reports</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Reimbursement
          </h1>
          <p className="text-sm text-ink-faint">
            Per-worker per-day reimbursable distance. Inter-visit driving is always reimbursable.
            Morning / evening legs are reimbursable only if they touch an office site (within 200
            m). Driving on a day with no visits is shown as unclassified — adjust manually if it
            should be paid.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label htmlFor="rr-team" className="mb-1 block text-[11px] font-medium text-ink-faint">
              Team
            </label>
            <select
              id="rr-team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="min-w-[12rem] rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            >
              <option value="">
                {me.data?.capabilities?.monitor || me.data?.capabilities?.admin
                  ? 'All teams'
                  : 'My teams'}
              </option>
              {allTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="rr-from" className="mb-1 block text-[11px] font-medium text-ink-faint">
              From
            </label>
            <input
              id="rr-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="rr-to" className="mb-1 block text-[11px] font-medium text-ink-faint">
              To
            </label>
            <input
              id="rr-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </div>
          <CsvDownloadButton
            from={fromIso}
            to={toIso}
            teamIds={teamId || undefined}
            view={view}
            workerSummaries={workerSummaries}
          />
        </div>
      </div>

      {/* View toggle. By worker is the payroll-ready summary; By day is the
          detail breakdown. The toggle stays visible whether or not data is
          loaded so the user can flip before triggering a fetch. */}
      <div className="mb-3 inline-flex gap-0.5 rounded-md border border-line bg-surface-1 p-1 shadow-xs">
        <button
          type="button"
          onClick={() => setView('by_worker')}
          className={cn(
            'rounded-sm px-3 py-1 text-xs font-semibold transition',
            view === 'by_worker' ? 'bg-brand-purple text-white' : 'text-ink-faint hover:text-ink',
          )}
        >
          By worker
        </button>
        <button
          type="button"
          onClick={() => setView('by_day')}
          className={cn(
            'rounded-sm px-3 py-1 text-xs font-semibold transition',
            view === 'by_day' ? 'bg-brand-purple text-white' : 'text-ink-faint hover:text-ink',
          )}
        >
          By day
        </button>
      </div>

      {report.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load report: {(report.error as Error).message}
        </div>
      )}

      {report.isLoading && (
        <div className="panel p-8 text-center text-sm text-ink-faint">Loading report…</div>
      )}

      {!report.isLoading && rows.length === 0 && !report.isError && (
        <EmptyState
          icon={Receipt}
          title="No driving in this window"
          description="No workers in scope had any classifiable driving for these dates. Try a wider window or a different team."
        />
      )}

      {view === 'by_worker' && rows.length > 0 && (
        <div className="panel">
          <div className="flex items-center justify-between border-b border-line px-4 py-3 text-xs">
            <span className="font-mono uppercase tracking-wider text-ink-faint">
              {workerSummaries.length} workers · {rows.length} worker-days · total{' '}
              {(totalTotalM / 1000).toFixed(1)} km driven
            </span>
            <span className="font-semibold text-ink">
              Reimbursable {(totalReimbM / 1000).toFixed(1)} km
            </span>
          </div>
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th className="text-right">Days worked</Th>
                <Th className="text-right">Visits</Th>
                <Th className="text-right">Total km</Th>
                <Th className="text-right">Inter-visit</Th>
                <Th className="text-right">Office bonus</Th>
                <Th className="text-right">Commute</Th>
                <Th className="text-right">Unclassified</Th>
                <Th className="text-right">Reimbursable</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {workerSummaries.map((s) => (
                <tr key={s.user_id} className="hover:bg-surface-3">
                  <Td>
                    <div className="text-sm font-medium text-ink">{s.display_name}</div>
                    {s.job_title && <div className="text-xs text-ink-faint">{s.job_title}</div>}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-dim">{s.daysWorked}</Td>
                  <Td className="text-right tabular-nums text-ink">{s.visitCount}</Td>
                  <Td className="text-right tabular-nums text-ink">
                    {(s.totalDistanceM / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-emerald-700">
                    {(s.interVisitM / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-emerald-700">
                    {(s.officeBonusM / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-mute">
                    {(s.commuteM / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-amber-700">
                    {(s.unclassifiedM / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums font-semibold text-ink">
                    {(s.reimbursableM / 1000).toFixed(1)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'by_day' && rows.length > 0 && (
        <div className="panel">
          <div className="flex items-center justify-between border-b border-line px-4 py-3 text-xs">
            <span className="font-mono uppercase tracking-wider text-ink-faint">
              {rows.length} worker-days · total {(totalTotalM / 1000).toFixed(1)} km driven
            </span>
            <span className="font-semibold text-ink">
              Reimbursable {(totalReimbM / 1000).toFixed(1)} km
            </span>
          </div>
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th>Date</Th>
                <Th className="text-right">Visits</Th>
                <Th className="text-right">Total km</Th>
                <Th className="text-right">Inter-visit</Th>
                <Th className="text-right">Office bonus</Th>
                <Th className="text-right">Commute</Th>
                <Th className="text-right">Unclassified</Th>
                <Th className="text-right">Reimbursable</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {rows.map((r) => (
                <tr key={`${r.user_id}-${r.date}`} className="hover:bg-surface-3">
                  <Td>
                    <div className="text-sm font-medium text-ink">{r.display_name}</div>
                    {r.job_title && <div className="text-xs text-ink-faint">{r.job_title}</div>}
                  </Td>
                  <Td className="text-sm text-ink-dim">{r.date}</Td>
                  <Td className="text-right tabular-nums text-ink">{r.visit_count}</Td>
                  <Td className="text-right tabular-nums text-ink">
                    {(r.total_distance_m / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-emerald-700">
                    {(r.inter_visit_m / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-emerald-700">
                    {(r.office_bonus_m / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-mute">
                    {(r.commute_m / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums text-amber-700">
                    {(r.unclassified_m / 1000).toFixed(1)}
                  </Td>
                  <Td className="text-right tabular-nums font-semibold text-ink">
                    {(r.reimbursable_m / 1000).toFixed(1)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function csvEscape(s: string): string {
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// Build the by-worker CSV client-side from the already-fetched summary rows.
// Saves an extra round-trip for what's a pure aggregation of data the browser
// already has. The by-day CSV still hits the server endpoint to avoid
// duplicating its serialisation here.
function workerSummariesToCsv(rows: WorkerSummary[]): string {
  const header = [
    'user_id',
    'display_name',
    'email',
    'job_title',
    'days_worked',
    'visit_count',
    'journey_count',
    'total_km',
    'inter_visit_km',
    'office_bonus_km',
    'commute_km',
    'unclassified_km',
    'reimbursable_km',
  ].join(',');
  const lines = rows.map((r) =>
    [
      r.user_id,
      csvEscape(r.display_name),
      csvEscape(r.email),
      csvEscape(r.job_title ?? ''),
      r.daysWorked.toString(),
      r.visitCount.toString(),
      r.journeyCount.toString(),
      (r.totalDistanceM / 1000).toFixed(2),
      (r.interVisitM / 1000).toFixed(2),
      (r.officeBonusM / 1000).toFixed(2),
      (r.commuteM / 1000).toFixed(2),
      (r.unclassifiedM / 1000).toFixed(2),
      (r.reimbursableM / 1000).toFixed(2),
    ].join(','),
  );
  return [header, ...lines].join('\n');
}

function downloadCsvBlob(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function CsvDownloadButton({
  from,
  to,
  teamIds,
  view,
  workerSummaries,
}: {
  from: string;
  to: string;
  teamIds?: string;
  view: View;
  workerSummaries: WorkerSummary[];
}) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    setBusy(true);
    try {
      const suffix = view === 'by_worker' ? 'by-worker' : 'by-day';
      const filename = `reimbursement-${suffix}-${from.slice(0, 10)}-to-${to.slice(0, 10)}.csv`;
      if (view === 'by_worker') {
        // Summaries are already in memory — no server round-trip needed.
        downloadCsvBlob(workerSummariesToCsv(workerSummaries), filename);
        return;
      }
      const token = await getToken();
      const sp = new URLSearchParams({ from, to });
      if (teamIds) sp.set('team_ids', teamIds);
      const res = await fetch(`${API_BASE_URL}/reports/reimbursement.csv?${sp.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      downloadCsvBlob(text, filename);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 text-sm font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50',
      )}
      title="Download as CSV"
    >
      <Download className="h-3.5 w-3.5" />
      {busy ? 'Preparing…' : 'CSV'}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
